
/*
  James & Co London — Dawn cart drawer, audited.

  Retains existing cart and overlay behaviour.
  Reconnects the header cart icon after Dawn replaces
  it during cart updates.
*/

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') this.close();
    });

    this.handleOverlayClick = this.close.bind(this);

    this.addEventListener('click', (event) => {
      if (event.target.closest('#CartDrawer-Overlay')) {
        this.handleOverlayClick();
      }
    });

    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');

    if (
      !cartLink ||
      cartLink.dataset.jcDrawerBound === 'true'
    ) {
      return;
    }

    cartLink.dataset.jcDrawerBound = 'true';

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (
        event.code === 'Space' ||
        event.key === ' ' ||
        event.code === 'Enter'
      ) {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
    }

    const cartDrawerNote = this.querySelector(
      '[id^="Details-"] summary'
    );

    if (
      cartDrawerNote &&
      !cartDrawerNote.hasAttribute('role')
    ) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    // Cancel any pending opening animation.
    if (this._jcOpenFrame) {
      cancelAnimationFrame(this._jcOpenFrame);
    }

    const overlay = this.querySelector(
      '#CartDrawer-Overlay'
    );

    if (overlay) {
      overlay.style.removeProperty('pointer-events');
    }

    document.body.classList.add(
      'jc-cart-drawer-open',
      'overflow-hidden'
    );

    this._jcOpenFrame = requestAnimationFrame(() => {
      this._jcOpenFrame = null;

      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        if (!this.classList.contains('active')) {
          return;
        }

        const container = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');

        const focusElement =
          this.querySelector('.drawer__inner') ||
          this.querySelector('.drawer__close');

        if (container && focusElement) {
          trapFocus(container, focusElement);
        }
      },
      { once: true }
    );
  }

  close() {
    // Ensure closing wins over a pending opening.
    if (this._jcOpenFrame) {
      cancelAnimationFrame(this._jcOpenFrame);
      this._jcOpenFrame = null;
    }

    this.classList.remove('active');

    // Prevent the backdrop blocking page interactions.
    const overlay = this.querySelector(
      '#CartDrawer-Overlay'
    );

    if (overlay) {
      overlay.style.pointerEvents = 'none';
    }

    document.body.classList.remove(
      'jc-cart-drawer-open',
      'overflow-hidden'
    );

    removeTrapFocus(this.activeElement);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    const detailsContent =
      cartDrawerNote.nextElementSibling;

    if (detailsContent && detailsContent.id) {
      cartDrawerNote.setAttribute(
        'aria-controls',
        detailsContent.id
      );
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget
          .closest('details')
          .hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener(
      'keyup',
      onKeyUpEscape
    );
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector('.drawer__inner');

    if (drawerInner) {
      drawerInner.classList.remove('is-empty');
    }

    // Reveal cart content after adding an item.
    this.classList.remove('is-empty');

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const target = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      const html = parsedState.sections?.[section.id];

      if (!target || !html) return;

      const updatedContent = this.getSectionInnerHTML(
        html,
        section.selector
      );

      if (updatedContent !== null) {
        target.innerHTML = updatedContent;
      }
    });

    // Reconnect the cart icon after its HTML is replaced.
    this.setHeaderCartIconAccessibility();

    this.open();
  }

  getSectionInnerHTML(
    html,
    selector = '.shopify-section'
  ) {
    const doc = new DOMParser().parseFromString(
      html,
      'text/html'
    );

    const element = doc.querySelector(selector);

    return element ? element.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(
    html,
    selector = '.shopify-section'
  ) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define(
    'cart-drawer-items',
    CartDrawerItems
  );
}
